import { motion } from "framer-motion";
import { Star, Medal } from "@phosphor-icons/react";
import { HERITAGE } from "@/lib/siteData";

export default function Heritage() {
  return (
    <section id="heritage" className="relative bg-navy/70 overflow-hidden" data-testid="heritage-section">
      {/* flag stripe top accent */}
      <div className="h-2 w-full flag-bar" />

      <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-36">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <Star size={16} weight="fill" className="text-blood" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-bone/70">
              / American Heritage
            </span>
            <Star size={16} weight="fill" className="text-bone" />
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-display uppercase leading-[0.88] tracking-tighter text-5xl md:text-7xl text-bone"
          >
            Forged By <span className="text-blood">Freedom</span>,<br />
            Carried By The <span className="flashy-text">Brave</span>
          </motion.h2>
          <p className="mt-8 font-body text-base md:text-lg text-bone/75 leading-relaxed">
            From the beaches of Normandy to the jungles of Vietnam and the deserts
            of the modern age, the American service rifle tells the story of a
            nation. We carry that legacy forward — and the M4 platform we build and
            service is the direct descendant of the rifle our fathers and
            grandfathers carried into the fight.
          </p>
        </div>

        {/* Vietnam feature block */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="mt-16 grid md:grid-cols-2 border border-bone/15 bg-navy/40"
          data-testid="vietnam-block"
        >
          <div className="relative min-h-[280px] overflow-hidden border-b md:border-b-0 md:border-r border-bone/15">
            <img
              src="https://images.unsplash.com/photo-1705733071557-4a951a1c0582?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Vietnam-era military helicopter"
              className="w-full h-full object-cover saturate-[1.1]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent" />
            <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/80 bg-blood px-2 py-1">
              1955 – 1975
            </span>
          </div>
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <Medal size={34} weight="fill" className="text-blood mb-5" />
            <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight text-bone">
              Honoring The Vietnam Vet
            </h3>
            <p className="mt-4 font-body text-bone/75 leading-relaxed">
              The M16 defined a generation of American service. Fast-firing and
              featherweight compared to the rifles before it, it went to war in the
              humidity and mud of Southeast Asia. We're proud to serve the veterans
              of that era — and every American who's answered the call since.
            </p>
            <p className="mt-4 font-serif italic text-xl text-bone/80">
              "All gave some. Some gave all." — We remember.
            </p>
          </div>
        </motion.div>

        {/* Rifle timeline */}
        <div className="mt-20">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px flex-1 bg-bone/20" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-bone/60">
              The American Service Rifle
            </span>
            <span className="h-px flex-1 bg-bone/20" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {HERITAGE.map((r, i) => (
              <motion.article
                key={r.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`heritage-rifle-${i}`}
                className="group border border-bone/15 bg-navy/50 flex flex-col hover:border-blood transition-colors duration-300"
              >
                <div className="relative h-44 overflow-hidden border-b border-bone/15">
                  <img
                    src={r.img}
                    alt={r.name}
                    loading="lazy"
                    className="w-full h-full object-cover saturate-[1.1] scale-105 group-hover:scale-100 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
                  <span className="absolute top-3 left-3 font-display text-2xl text-bone drop-shadow">
                    {r.year}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h4 className="font-display uppercase text-2xl tracking-tight text-bone leading-none">
                    {r.name}
                  </h4>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-bone/80 bg-blood/80 px-2 py-1">
                      {r.war}
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-navy bg-brass px-2 py-1">
                      {r.cal}
                    </span>
                  </div>
                  <p className="mt-4 font-body text-sm text-bone/65 leading-relaxed">{r.note}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>

      <div className="h-2 w-full flag-bar" />
    </section>
  );
}
